// public/js/features/assets/asset-trips.js
// "Trips" section on the asset detail page.
//
// The API serves the same shape from either engine (handlers/trips.mjs), so
// this file does not know or care which one produced a row - except for the
// extra fields v2 adds, which are all about HOW MUCH TO TRUST the row:
// confidence, provisional, interpolated metres, second-hand positions, a
// start that was cut off by the window, a trail that went cold. A number
// that may still move has to say so (docs/trip-detection.md).
//
// Places are resolved from POIs and geofences AT RUNTIME rather than from the
// name stored when the trip was written — draw a geofence around a workshop
// today and every past trip that started there reads "Workshop XY" straight
// away. Coordinates are only ever a last resort; they tell a reader nothing.
import { formatMeasure } from '../../utils/units.js';
import { escapeHtml } from '../../utils/escape.js';
import { loadPlaces, locate } from '../pois/poi-locate.js';
import { renderTripMinimap } from './trip-minimap.js';
import { renderTripShare } from './trip-share.js';

const fmtDur = (s) => {
  if (s == null) return '—';
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  return h ? `${h} h ${m} min` : `${m} min`;
};

const coords = (lat, lon) =>
  (lat != null && lon != null) ? `${Number(lat).toFixed(4)}, ${Number(lon).toFixed(4)}` : '—';

/** Place name for a coordinate; falls back to the stored name, then coordinates. */
function placeLabel(pois, lat, lon, storedName) {
  const hit = locate(lat, lon, pois);
  if (hit) {
    if (hit.exact) return `<span title="Inside this geofence">${escapeHtml(hit.name)}</span>`;
    // No geofence covers this point — name the nearest place but SHOW the
    // distance, so an approximation is never read as a confirmed location.
    const away = hit.distanceM >= 1000
      ? `${(hit.distanceM / 1000).toFixed(1)} km`
      : `${hit.distanceM} m`;
    return `<span class="text-body-secondary" title="No geofence covers this point — nearest POI is ${away} away">`
         + `${escapeHtml(hit.name)} <span class="small">(~${away})</span></span>`;
  }
  if (storedName) return escapeHtml(storedName);
  return `<span class="text-muted font-monospace small">${coords(lat, lon)}</span>`;
}

export async function renderAssetTripsSection(container, assetUUID) {
  container.innerHTML = '<div class="text-muted small">Loading trips…</div>';

  let journeys = [];
  // Which detector produced this list. Shown while both exist so a
  // side-by-side comparison during the rollout is not guesswork; remove it
  // with the old engine (docs/trip-detection.md, step 8).
  let engine = null;
  try {
    const res = await window.authenticatedFetch(`api/v1/assets/${encodeURIComponent(assetUUID)}/trips?limit=50`);
    if (!res.ok) throw new Error('Failed to load trips');
    const body = await res.json();
    journeys = body.trips || body.journeys || [];
    engine = body.engine || null;
  } catch {
    container.innerHTML = '<div class="text-danger small">Failed to load trips.</div>';
    return;
  }

  if (!journeys.length) {
    container.innerHTML = '<div class="text-muted small">No trips recorded yet. Trips are derived from the positions the tracker reports, so one appears once the asset has moved between two places.</div>';
    return;
  }

  // Never let a POI failure hide the trips.
  let pois = [];
  try { pois = await loadPlaces(); } catch { pois = []; }

  const L = navigator.language;
  const dayTime  = (iso) => new Date(iso).toLocaleString(L, { dateStyle: 'medium', timeStyle: 'short' });
  const timeOnly = (iso) => new Date(iso).toLocaleTimeString(L, { hour: '2-digit', minute: '2-digit' });

  const engineNote = engine
    ? `<div class="small text-muted mb-2">${engine === 'v2'
        ? 'Derived from reported positions. The tracker’s own trip events are used as evidence, not as the last word.'
        : 'Built from the tracker’s trip start and end events.'}</div>`
    : '';

  container.innerHTML = engineNote + `<div class="list-group">${journeys.map((j, i) => {
    const multiDay = new Date(j.startTime).toDateString() !== new Date(j.endTime).toDateString();
    const stops = j.stops || [];
    const segs  = j.segmentCount || 1;
    const from  = placeLabel(pois, j.startLat, j.startLon, j.startPoi);
    const to    = placeLabel(pois, j.endLat,   j.endLon,   j.endPoi);
    const totalS = (j.drivingSeconds || 0) + (j.stoppedSeconds || 0);
    const isReloc = j.mode === 'relocation';
    // Average road speed is meaningless for a flight/shipment, so it is only
    // shown for drives.
    const avgKmh = (!isReloc && j.distanceM != null && j.drivingSeconds > 0)
      ? (j.distanceM / 1000) / (j.drivingSeconds / 3600) : null;

    return `
      <div class="list-group-item py-2" data-trip="${i}">
        <div class="d-flex flex-wrap align-items-center gap-2" style="cursor:pointer"
             data-bs-toggle="collapse" data-bs-target="#jny-${i}" role="button" aria-expanded="false">
          <span class="text-muted small text-nowrap">${dayTime(j.startTime)}${multiDay ? ` → ${dayTime(j.endTime)}` : ` – ${timeOnly(j.endTime)}`}</span>
          <span class="fw-semibold small">
            ${isReloc ? '<i class="fa-solid fa-plane-up text-info me-1" title="Relocation — flown or shipped, not driven"></i>' : ''}
            ${from} <i class="fa-solid fa-arrow-right-long text-muted mx-1"></i> ${to}</span>
          <span class="ms-auto d-flex gap-1 flex-wrap">
            ${j.openEnded ? '<span class="badge badge-soft-green" title="This trip has not ended yet - the asset has not stayed anywhere long enough to arrive">ongoing</span>' : ''}
            ${j.provisional ? '<span class="badge badge-soft-amber" title="Detected live at ingest. The scheduled pass may still adjust it once any late data arrives">provisional</span>' : ''}
            ${j.positionQuality === 'secondhand' ? '<span class="badge badge-soft-gray" title="Positions come from the gateway that heard this beacon, not from a GPS fix of its own">beacon-derived</span>' : ''}
            ${isReloc ? '<span class="badge badge-soft-blue" title="Transported, not driven">transport</span>' : ''}
            <span class="badge badge-soft-blue">${j.distanceM != null ? formatMeasure(j.distanceM / 1000, 'km', 1) : '—'}</span>
            <span class="badge badge-soft-gray">${fmtDur(totalS)}</span>
            ${isReloc ? '' : `<span class="badge badge-soft-amber">${stops.length} stop${stops.length === 1 ? '' : 's'}</span>`}
          </span>
        </div>

        <div class="collapse" id="jny-${i}">
          <div class="trip-map-slot mt-2"></div>
          <div class="trip-share-slot"></div>

          <div class="small text-muted mt-2 d-flex flex-wrap gap-3">
            ${isReloc
              ? `<span class="text-info-emphasis"><i class="fa-solid fa-plane-up me-1"></i>Relocation — straight-line distance across a transport the tracker did not record en route</span>
                 <span>Transit ${fmtDur(totalS)}</span>`
              : `<span>Driving ${fmtDur(j.drivingSeconds)}</span>
                 <span>Stopped ${fmtDur(j.stoppedSeconds)}</span>
                 ${avgKmh != null ? `<span>Ø ${formatMeasure(avgKmh, 'km/h', 0)}</span>` : ''}
                 <span>${segs} leg${segs === 1 ? '' : 's'}</span>
                 ${j.interpolatedM ? `<span class="text-warning-emphasis" title="Part of the distance is a straight line across a stretch the tracker did not report">
                   ${formatMeasure(j.interpolatedM / 1000, 'km', 1)} interpolated</span>` : ''}
                 ${j.sparseHistory ? `<span class="text-warning-emphasis" title="The surviving fixes are too far apart to describe the real route — the distance is a lower bound">coarse history</span>` : ''}`}
          </div>

          ${(j.truncatedStart || j.endedBy === 'gap' || j.confidence != null) ? `
          <div class="small text-muted mt-2 d-flex flex-wrap gap-3">
            ${j.confidence != null ? `<span title="How much of this trip was observed rather than inferred: device-confirmed boundaries raise it, interpolated stretches and coarse sampling lower it">Confidence ${Math.round(j.confidence * 100)}%</span>` : ''}
            ${j.truncatedStart ? '<span class="text-warning-emphasis" title="The window this was derived from began mid-move, so the real start is earlier">start cut off</span>' : ''}
            ${j.endedBy === 'gap' ? '<span class="text-warning-emphasis" title="The tracker went silent for long enough that nothing can be said about what happened next">trail went cold</span>' : ''}
          </div>` : ''}

          ${(j.legs && j.legs.length > 1) ? `
          <ul class="list-unstyled small mb-0 mt-2">
            ${j.legs.map((l, n) => `<li class="py-1 border-top">
              <span class="text-muted">Leg ${n + 1}</span>
              ${timeOnly(l.startTime)}–${timeOnly(l.endTime)}
              · ${l.distanceM != null ? formatMeasure(l.distanceM / 1000, 'km', 1) : '—'}
              ${l.startSource === 'device' || l.endSource === 'device'
                ? '<span class="text-body-secondary" title="The tracker reported this boundary itself, so it is timed more exactly than we could infer">· device-confirmed</span>' : ''}
              ${l.interpolatedM ? `<span class="text-warning-emphasis" title="Part of this leg is a straight line across a stretch the tracker did not report">· ${formatMeasure(l.interpolatedM / 1000, 'km', 1)} interpolated</span>` : ''}
            </li>`).join('')}
          </ul>` : ''}

          ${isReloc ? '' : (stops.length ? `<ul class="list-unstyled small mb-0 mt-2">${stops.map(s => `
            <li class="py-1 border-top">⏸ ${placeLabel(pois, s.lat, s.lon, s.poiName)} — ${fmtDur(s.durationS)}
              <span class="text-muted">(${timeOnly(s.startTime)}–${timeOnly(s.endTime)})</span></li>`).join('')}</ul>`
            : '<div class="small text-muted mt-2">Non-stop.</div>')}
        </div>
      </div>`;
  }).join('')}</div>`;

  // Build each map only when its panel is first opened — 50 hidden MapLibre
  // instances would be pointless work and a lot of WebGL contexts.
  container.querySelectorAll('.collapse').forEach((panel, i) => {
    panel.addEventListener('shown.bs.collapse', async () => {
      const slot = panel.querySelector('.trip-map-slot');
      if (!slot || slot.dataset.rendered) return;
      slot.dataset.rendered = '1';
      try { await renderTripMinimap(slot, assetUUID, journeys[i]); }
      catch { slot.innerHTML = '<div class="text-muted small">Map unavailable.</div>'; }
      // The share box loads with the panel, not with the list: 50 trips
      // would otherwise mean 50 requests to render one page.
      try { await renderTripShare(panel.querySelector('.trip-share-slot'), assetUUID, journeys[i]); }
      catch { /* sharing is not worth breaking the trip for */ }
    }, { once: false });
  });
}
